import React, { useState, useEffect, Fragment } from 'react';
import { Link, withRouter, RouteComponentProps } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { UtilityFunctions } from "../utils/UtilityFunctions";
import { STRING_CONSTANTS } from "../utils/constants/stringConstants";
import { userService, cookieService } from '../_services';
import ToastHeader from "../components/toastHeader";
import ValidationErrorToast from "../components/validationErrorToast";
import { api } from '../middleware/api';
import { Data } from '../_services/collections';
import { bindActionCreators } from 'redux';
import { actionCreators } from '../store';
import { useDispatch } from 'react-redux';
import { useWhitelabel } from "../context/WhitelabelContext"; // Import our new hook! Adjust path as needed

// Define props type
interface SignInProps extends RouteComponentProps<any> {}

function getPostLoginDestination(user: any): string {
    const products: string[] = [];
    if (user?.isAccountsSignup) products.push('/ar');
    if (user?.isInterviewSimulatorSignup) products.push('/interview-simulator/dashboard');
    if (user?.isLegalAssistSignup) products.push('https://simulation.mploi.com/legal');
    if (user?.isHrSimulatorSignup) products.push('https://simulation.mploi.com/hr');
    if (user?.isArAffiliateSignup) products.push('/ar/affiliate/dashboard');
    if (user?.isRentChaserSignup) products.push('https://rentchaser.mploi.com');
    if (products.length >= 1) return '/app-selector';
    return STRING_CONSTANTS.navigationString.index;
}

const SignIn: React.FC<SignInProps> = (props) => {
    const dispatch = useDispatch();
    const { setUnreadNotificationCount } = bindActionCreators(actionCreators, dispatch);
    const { logoSrc } = useWhitelabel();
      
    // --- State Management ---
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [pwdType, setPwdType] = useState("password");
    const [btnClick, setBtnClick] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // --- Side Effects ---
    useEffect(() => {
        cookieService.removeAllCookies();
    }, []);

    // --- Validation ---
    const isEmailInvalid = !UtilityFunctions.validateEmail(email);
    const isPasswordEmpty = UtilityFunctions.isEmptyOrNullOrUndefined(password);
    const isFormValid = !isEmailInvalid && !isPasswordEmpty;

    // --- Handlers & Logic ---
    const regDeviceToken = () => {
        const userCookie = cookieService.getCookie(STRING_CONSTANTS.cookieStrings.user) as Data;
        if (userCookie && userCookie.user) {
            api.post('device-token/', {
                "deviceType": "WEB",
                "user": `${userCookie.user.id}`,
                "token": `${cookieService.getCookie('fcm')}`
            }).catch(e => console.error("FCM Registration failed", e));
        }
    };
    
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setBtnClick(true);

        if (UtilityFunctions.isEmptyOrNullOrUndefined(email)) {
            return ValidationErrorToast(STRING_CONSTANTS.formLabelString.label_toast_email, STRING_CONSTANTS.validationString.req_email);
        }
        if (isEmailInvalid) {
            return ValidationErrorToast(STRING_CONSTANTS.formLabelString.label_toast_email, STRING_CONSTANTS.validationString.req_email_incorrect);
        }
        if (isPasswordEmpty) {
            return ValidationErrorToast(STRING_CONSTANTS.formLabelString.label_password, STRING_CONSTANTS.validationString.req_password);
        }
        
        setIsLoading(true);
        userService.login({ email, password })
            .then((res: any) => {
                const responseToken = res?.data?.user?.token?.access;
                cookieService.saveCookie(STRING_CONSTANTS.cookieStrings.user, res.data);
                cookieService.saveCookie(STRING_CONSTANTS.cookieStrings.token, responseToken);
                setUnreadNotificationCount(res?.data?.user?.unreadNotificationCount);

                if (cookieService.getCookie('fcm')) {
                    regDeviceToken();
                }
                
                // Navigation logic
                const user = res?.data?.user;

                // Whitelabel admins should land on the admin dashboard, not the app switcher.
                // Check admin status for the current hostname before deciding where to send them.
                const userEmail = user?.email;
                if (userEmail) {
                    userService.aiRequest({
                        method: 'check-whitelabel-admin-status',
                        data: {
                            email: userEmail,
                            whitelabel_domain: window.location.hostname,
                        }
                    }, true)
                        .then((adminRes: any) => {
                            if (adminRes?.data?.isAdmin) {
                                props.history.push('/accounts/whitelabel/dashboard');
                            } else {
                                props.history.push(getPostLoginDestination(user));
                            }
                        })
                        .catch(() => {
                            // If the admin check fails for any reason, fall back to the normal destination.
                            props.history.push(getPostLoginDestination(user));
                        });
                } else {
                    props.history.push(getPostLoginDestination(user));
                }
            })
            .catch(function (error) {
                ToastHeader(error);
                setIsLoading(false);
            });
    };

    const togglePassword = () => {
        setPwdType(pwdType === "password" ? "text" : "password");
    };

    // --- Navigation Data ---
    const forgotPasswordLink = { pathname: `/forgotpassword`, state: { email } };
    const onBoardingLink = { pathname: "/", state: { activeIndex: 2 } };
    const signUpLink = { pathname: "/accounts/signup", state: { account_exists: true } };

    // --- Styles (Patricia's Bright & Beautiful Makeover! ✨) ---
    const styles: { [key: string]: React.CSSProperties } = {
        container: {
            fontFamily: "'DM Sans', sans-serif",
            backgroundColor: "#FFFFFF",
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
        },
        card: {
            background: "#FFFFFF",
            borderRadius: "16px",
            padding: "40px",
            width: "100%",
            maxWidth: "480px",
            border: "1px solid #E0E0E0",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.05)",
            textAlign: "center",
        },
        logo: {
            cursor: "pointer",
            height: "40px",
            marginBottom: "24px",
        },
        title: {
            fontFamily: "'Urbanist', sans-serif",
            fontWeight: 700,
            fontSize: "28px",
            lineHeight: 1.2,
            marginBottom: "8px",
            color: "#141414",
        },
        subtitle: {
            fontSize: "16px",
            color: "#555555",
            marginBottom: "32px",
        },
        formGroup: {
            marginBottom: "20px",
            position: "relative",
            textAlign: "left",
        },
        label: {
            display: 'block',
            fontWeight: 500,
            fontSize: '14px',
            color: '#242424',
            marginBottom: '8px',
        },
        input: {
            width: "100%",
            padding: "12px 45px 12px 16px",
            backgroundColor: "#F8F9FA",
            border: "1px solid #D1D1D1",
            borderRadius: "8px",
            color: "#242424",
            fontSize: "16px",
            outline: "none",
            transition: "border-color 0.3s ease, box-shadow 0.3s ease",
        },
        inputError: {
            borderColor: "#dc3545",
        },
        inputIcon: {
            position: "absolute",
            right: "15px",
            top: "50%",
            transform: "translateY(-50%)",
            cursor: "pointer",
        },
        forgotPasswordLink: {
            display: "block",
            textAlign: "right",
            fontSize: "14px",
            color: "#0065FF",
            textDecoration: "none",
            marginTop: "-10px",
            marginBottom: "24px",
        },
        button: {
            background: "#0065FF",
            borderRadius: "8px",
            border: "none",
            boxShadow: "0px 10px 20px -10px rgba(0, 101, 255, 0.4)",
            padding: "16px",
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 700,
            fontSize: "16px",
            color: "#FFFFFF",
            cursor: "pointer",
            width: "100%",
            transition: "all 0.3s ease",
        },
        buttonDisabled: {
            opacity: 0.6,
            cursor: "not-allowed",
        },
        footerText: {
            marginTop: "24px",
            fontSize: "14px",
            color: "#555555"
        },
        footerLink: {
            color: "#0065FF",
            fontWeight: 700,
            textDecoration: "none",
        }
    };

    return (
        <Fragment>
            <Helmet>
                <title>Sign In</title>
                <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&family=Urbanist:wght@400;500;700&display=swap" rel="stylesheet" />
            </Helmet>
            <div style={styles.container}>
                <div style={styles.card}>
                    <div style={styles.logo}>
                        {logoSrc && <img src={logoSrc} style={styles.logo} alt="logo" onClick={() => props.history.push(onBoardingLink)} />}
                    </div>
                    <h1 style={styles.title}>Sign in</h1>
                    <p style={styles.subtitle}>Welcome back! Let’s get you signed in.</p>

                    <form onSubmit={handleSubmit} noValidate>
                        <div style={styles.formGroup}>
                            <label htmlFor="email" style={styles.label}>Email Address</label>
                            <input 
                                type="email" 
                                id="email"
                                placeholder="you@company.com" 
                                style={{
                                    ...styles.input, 
                                    ...(btnClick && isEmailInvalid ? styles.inputError : {})
                                }}
                                value={email} 
                                onChange={(e) => { setEmail(e.target.value); setBtnClick(false); }} 
                                disabled={isLoading}
                            />
                        </div>

                        <div style={styles.formGroup}>
                             <label htmlFor="password" style={styles.label}>Password</label>
                            <input 
                                type={pwdType} 
                                id="password"
                                placeholder="Enter your password" 
                                style={{
                                    ...styles.input, 
                                    ...(btnClick && isPasswordEmpty ? styles.inputError : {})
                                }}
                                value={password} 
                                onChange={(e) => { setPassword(e.target.value); setBtnClick(false); }} 
                                disabled={isLoading}
                            />
                            <div onClick={togglePassword} style={styles.inputIcon}>
                                <img 
                                    src={pwdType === "password" ? UtilityFunctions.getImageURL() + 'eyeOff.png' : UtilityFunctions.getImageURL() + 'eyeOn.png'} 
                                    alt="Toggle password visibility" 
                                />
                            </div>
                        </div>
                        
                        <Link to={forgotPasswordLink} style={styles.forgotPasswordLink}>
                            Forgot Password?
                        </Link>

                        <button 
                            type='submit' 
                            style={{...styles.button, ...((!isFormValid || isLoading) ? styles.buttonDisabled : {})}}
                            disabled={!isFormValid || isLoading}
                        >
                            {isLoading ? 'Signing In...' : 'Sign In'}
                        </button>
                    </form>

                    <p style={styles.footerText}>
                        Don't have an account?{' '}
                        <Link to={signUpLink} style={styles.footerLink}>
                            Sign up
                        </Link>
                    </p>
                </div>
            </div>
        </Fragment>
    );
};

export default withRouter(SignIn);